import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Watch, ArrowRight, Check } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  React.useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const features = [
    'Inventory, CRM, and accounting in one place',
    'Client wishlists and dealer network',
    'Professional invoicing — no QuickBooks required',
    'Built for the watch trade',
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06] bg-[#0a0a0a]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
              <Watch className="w-4 h-4 text-[#0a0a0a]" />
            </div>
            <span className="font-semibold text-[15px] tracking-tight">Dial-Grid</span>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="text-sm font-medium text-white/80 hover:text-white transition-colors"
          >
            Sign in
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white leading-[1.1] mb-6">
            The one platform for
            <br />
            <span className="text-white/70">luxury watch dealers</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/50 max-w-xl mx-auto mb-10 leading-relaxed">
            Inventory, clients, sales, and accounting. No spreadsheets, no QuickBooks. Just one place to run your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-white text-[#0a0a0a] font-medium text-sm hover:bg-white/90 transition-colors"
            >
              Get started
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-white/15 text-white/80 font-medium text-sm hover:bg-white/5 hover:text-white transition-colors"
            >
              See what’s included
            </button>
          </div>
        </div>
      </section>

      {/* Features - minimal list */}
      <section id="features" className="py-20 px-6 border-t border-white/[0.06]">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-sm font-medium text-white/40 uppercase tracking-wider mb-8 text-center">
            Everything you need
          </h2>
          <ul className="space-y-4">
            {features.map((line, i) => (
              <li key={i} className="flex items-center gap-3 text-white/70">
                <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-white/80" />
                </span>
                {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pricing strip */}
      <section className="py-16 px-6 border-t border-white/[0.06]">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-white/50 text-sm mb-2">Simple pricing</p>
          <p className="text-3xl font-semibold text-white tracking-tight">
            $99<span className="text-lg font-normal text-white/50">/month</span>
          </p>
          <p className="text-white/40 text-sm mt-1">7-day free trial · No credit card required</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-white text-[#0a0a0a] font-medium text-sm hover:bg-white/90 transition-colors"
          >
            Start free trial
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/[0.06] bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Terms</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" className="text-sm text-white/80 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-white/80 hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/usage-policy" className="text-sm text-white/80 hover:text-white transition-colors">
                  Usage Policy
                </Link>
              </li>
              <li>
                <Link to="/data-destruction-policy" className="text-sm text-white/80 hover:text-white transition-colors">
                  Data Destruction Policy
                </Link>
              </li>
            </ul>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-white/[0.06]">
            <div className="flex items-center gap-2">
              <Watch className="w-4 h-4 text-white/40" />
              <span className="text-sm text-white/40">Dial-Grid</span>
            </div>
            <p className="text-sm text-white/30">&copy; {new Date().getFullYear()} Dial-Grid</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
